/**
 * Ticket Template System - Sample Content
 *
 * Pre-defined sample content templates for ticket descriptions.
 * Content is prefilled when a ticket type is selected.
 *
 * Naming convention:
 * - 'group:{group_name}' - Base content for a group
 * - 'key:{ticket_type_key}' - Override content for specific key
 */

/**
 * Sample content templates registry
 * Keys prefixed with 'group:' are base templates per group
 * Keys prefixed with 'key:' are overrides for specific ticket type keys
 */
export const SAMPLE_CONTENT: Record<string, string> = {
    // ============================================
    // GROUP TEMPLATES
    // ============================================

    'group:error_service': `## Mô tả sự cố
- **Thời gian phát hiện:**
- **Service bị ảnh hưởng:**
- **Mô tả lỗi:**

## Tác động
- **Phạm vi ảnh hưởng:**
- **Mức độ nghiêm trọng:**

## Thông tin bổ sung
- **Log/Screenshot:** (nếu có)`,

    'group:service_request': `## Yêu cầu thay đổi
- **Loại thay đổi:**
- **Service liên quan:**
- **Mô tả chi tiết:**

## Lý do yêu cầu
- **Mục đích:**

## Thời gian mong muốn
- **Ngày thực hiện:**`,

    'group:support_request': `## Yêu cầu hỗ trợ
- **Vấn đề cần hỗ trợ:**
- **Mô tả chi tiết:**

## Thông tin bổ sung
- **Đã thử các cách:** (nếu có)`,

    'group:service': `## Thông tin dịch vụ
- **Loại dịch vụ:**
- **Cấu hình yêu cầu:**

## Ghi chú
- **Yêu cầu đặc biệt:** (nếu có)`,

    'group:meeting': `## Thông tin cuộc họp
- **Mục đích:**
- **Nội dung thảo luận:**
- **Người tham dự:**

## Chuẩn bị
- **Tài liệu cần chuẩn bị:** (nếu có)`,

    'group:change': `## Yêu cầu thay đổi (RFC)
- **Loại thay đổi:**
- **Hệ thống/Service:**
- **Mô tả chi tiết:**

## Đánh giá rủi ro
- **Tác động tiềm ẩn:**
- **Kế hoạch rollback:**

## Thời gian
- **Thời điểm thực hiện:**
- **Thời gian downtime dự kiến:**`,

    'group:internal': `## Nội dung
- **Mô tả:**

## Yêu cầu
- **Cần thực hiện:**`,

    // ============================================
    // KEY-SPECIFIC OVERRIDES
    // ============================================

    'key:error_service_incident': `## Báo cáo sự cố nghiêm trọng

### Thông tin sự cố
- **Thời gian bắt đầu:**
- **Service bị ảnh hưởng:**
- **Mô tả sự cố:**

### Đánh giá tác động
- **Số lượng khách hàng ảnh hưởng:**
- **Doanh thu ảnh hưởng (nếu có):**
- **Mức độ nghiêm trọng:** [Critical/High/Medium]

### Hành động khẩn cấp
- **Đã thực hiện:**
- **Cần thực hiện tiếp:**

### Thông tin liên hệ
- **Người báo cáo:**
- **Người chịu trách nhiệm:**`,

    'key:service_creating': `## Thông tin tạo dịch vụ mới

### Thông tin cơ bản
- **Tên portal/Project:**
- **Gói dịch vụ:**

### Cấu hình yêu cầu
- **Bandwidth:**
- **Storage:**
- **Các tính năng bổ sung:**

### Thời gian
- **Ngày bắt đầu mong muốn:**`,

    'key:service_creating_poc': `## Thông tin tạo dịch vụ POC

### Thông tin POC
- **Mục đích POC:**
- **Thời gian POC:**
- **Tiêu chí đánh giá:**

### Cấu hình yêu cầu
- **Gói dịch vụ:**
- **Các tính năng cần test:**

### Kế hoạch sau POC
- **Chuyển đổi sang production:** [Có/Không/Chưa xác định]`,

    'key:service_cancelled': `## Yêu cầu hủy dịch vụ

### Thông tin dịch vụ
- **Tên service:**
- **Lý do hủy:**

### Xác nhận
- **Đã backup dữ liệu:** [Có/Không/Không cần]
- **Ngày dừng dịch vụ mong muốn:**`,

    'key:service_suspend': `## Yêu cầu tạm dừng dịch vụ

### Thông tin
- **Tên service:**
- **Lý do tạm dừng:**

### Thời gian
- **Ngày tạm dừng:**
- **Ngày dự kiến mở lại:**

### Lưu ý
- **Yêu cầu đặc biệt khi tạm dừng:**`,

    'key:internal_problem': `## Báo cáo vấn đề nội bộ

### Mô tả vấn đề
- **Vấn đề gặp phải:**
- **Ảnh hưởng:**

### Thông tin bổ sung
- **Bộ phận liên quan:**
- **Ưu tiên:** [Cao/Trung bình/Thấp]`,

    'key:meeting': `## Thông tin cuộc họp

### Chi tiết
- **Chủ đề:**
- **Mục đích:**

### Người tham dự
- **Từ công ty:**
- **Từ khách hàng:**

### Agenda
1.
2.
3.

### Ghi chú
- **Tài liệu cần chuẩn bị:**`,
}

/**
 * Get sample content for a ticket type
 * @param group - Ticket type group
 * @param key - Optional ticket type key for specific content
 * @returns Sample content string
 */
export function getSampleContent(group: string, key?: string): string {
    // Key-specific content takes priority
    if (key && SAMPLE_CONTENT[`key:${key}`]) {
        return SAMPLE_CONTENT[`key:${key}`]
    }

    // Fall back to group content
    if (SAMPLE_CONTENT[`group:${group}`]) {
        return SAMPLE_CONTENT[`group:${group}`]
    }

    // Default empty
    return ''
}
